import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Tab,
  Tabs
} from 'react-bootstrap';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const SchoolInfoManager = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [generalInfo, setGeneralInfo] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState({ general: false, documents: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  const [formData, setFormData] = useState({
    // General info fields
    title: '',
    information: '',
    // Document fields
    doc_name: '',
    file_path: '',
    remarks: '',
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    if (activeTab === 'general') {
      fetchGeneralInfo();
    } else {
      fetchDocuments();
    }
  }, [activeTab]);

  const resetForm = () => {
    setFormData({
      title: '',
      information: '',
      doc_name: '',
      file_path: '',
      remarks: '',
      display_order: 0,
      is_active: true
    });
    setUploadedFile(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchGeneralInfo = async () => {
    try {
      setLoading(prev => ({ ...prev, general: true }));
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/school_info.php?type=general`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setGeneralInfo(response.data.data);
      } else {
        setError('Failed to load general information');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, general: false }));
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(prev => ({ ...prev, documents: true }));
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/school_info.php?type=documents&admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedDocs = response.data.data.map(doc => ({
          ...doc,
          doc_id: parseInt(doc.doc_id),
          display_order: parseInt(doc.display_order),
          is_active: doc.is_active === "1" || doc.is_active === 1
        })).sort((a, b) => a.display_order - b.display_order);
        
        setDocuments(processedDocs);
      } else {
        setError('Failed to load documents');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, documents: false }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const uploadFile = async (file) => {
    try {
      setUploadingFile(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', file);
      
      const response = await axios.post(`${API_BASE_URL}/upload_document.php`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        return response.data.file_path;
      }
      return null;
    } catch (err) {
      console.error('File upload error:', err);
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let dataToSend = { ...formData };
      
      // Handle file upload for documents
      if (activeTab === 'documents' && uploadedFile) {
        const uploadedPath = await uploadFile(uploadedFile);
        if (uploadedPath) {
          dataToSend.file_path = uploadedPath;
        } else {
          setError('Failed to upload file');
          return;
        }
      }
      
      // Set display order if not specified
      if (activeTab === 'documents' && (!dataToSend.display_order || dataToSend.display_order <= 0)) {
        const maxOrder = documents.length > 0 
          ? Math.max(...documents.map(doc => parseInt(doc.display_order))) 
          : 0;
        dataToSend.display_order = maxOrder + 1;
      }
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingItem) {
        // Update existing
        const id = activeTab === 'general' ? editingItem.sl_no : editingItem.doc_id;
        response = await axios.put(
          `${API_BASE_URL}/school_info.php?id=${id}&type=${activeTab}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Create new
        response = await axios.post(`${API_BASE_URL}/school_info.php?type=${activeTab}`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingItem ? 'Item updated!' : 'Item added!');
        setShowModal(false);
        resetForm();
        setEditingItem(null);
        
        // Refresh data
        if (activeTab === 'general') {
          fetchGeneralInfo();
        } else {
          fetchDocuments();
        }
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id, type) => {
    if (window.confirm(`Are you sure you want to delete this ${type === 'general' ? 'information' : 'document'}?`)) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `${API_BASE_URL}/school_info.php?id=${id}&type=${type === 'general' ? 'general' : 'documents'}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.data.success) {
          setSuccess(`${type === 'general' ? 'Information' : 'Document'} deleted!`);
          
          // Refresh data
          if (type === 'general') {
            fetchGeneralInfo();
          } else {
            fetchDocuments();
          }
          
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting item');
        console.error(err);
      }
    }
  };

  const handleEdit = (item, type) => {
    setEditingItem(item);
    
    if (type === 'general') {
      setFormData({
        title: item.title,
        information: item.information
      });
    } else {
      setFormData({
        doc_name: item.doc_name,
        file_path: item.file_path,
        remarks: item.remarks,
        display_order: parseInt(item.display_order),
        is_active: item.is_active === true || item.is_active === "1" || item.is_active === 1
      });
    }
    
    setShowModal(true);
  };

  const handleToggleActive = async (doc) => {
    try {
      const currentActive = doc.is_active === true || doc.is_active === "1" || doc.is_active === 1;
      const newActiveStatus = !currentActive;
      
      const updatedData = {
        doc_name: doc.doc_name,
        file_path: doc.file_path,
        remarks: doc.remarks,
        display_order: parseInt(doc.display_order),
        is_active: newActiveStatus ? 1 : 0
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/school_info.php?id=${doc.doc_id}&type=documents`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchDocuments();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling active status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    resetForm();
  };

  const onDragEnd = async (result) => {
    if (!result.destination || activeTab !== 'documents') return;
    
    const items = Array.from(documents);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index + 1
    }));
    
    setDocuments(updatedItems);
    
    // Send update to server
    try {
      const token = localStorage.getItem('token');
      const reorderData = updatedItems.map(item => ({
        doc_id: item.doc_id,
        display_order: item.display_order
      }));
      
      const response = await axios.post(`${API_BASE_URL}/school_info.php?type=documents`, {
        reorder: reorderData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.success) {
        setError('Failed to save order changes');
        fetchDocuments(); // Revert to server state
      }
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to save order changes');
      fetchDocuments(); // Revert to server state
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFileIcon = (filePath) => {
    if (!filePath) return 'fa-file';
    
    const ext = filePath.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf': return 'fa-file-pdf';
      case 'doc':
      case 'docx': return 'fa-file-word';
      case 'xls':
      case 'xlsx': return 'fa-file-excel';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return 'fa-file-image';
      case 'txt': return 'fa-file-alt';
      default: return 'fa-file';
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading.general || loading.documents) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading data...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>School Information Manager</h2>
            <p className="text-muted">Manage General Information and Public Disclosure Documents</p>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="general" title="General Information">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>School General Information</span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  <i className="fas fa-plus me-1"></i> Add Information
                </Button>
              </Card.Header>
              <Card.Body>
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th width="60">Sl No.</th>
                      <th>Title</th>
                      <th>Information</th>
                      <th width="150">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generalInfo.map((item) => (
                      <tr key={item.sl_no}>
                        <td><Badge bg="secondary">{item.sl_no}</Badge></td>
                        <td><strong>{item.title}</strong></td>
                        <td>
                          <small className="text-muted">
                            {truncateText(item.information, 80)}
                          </small>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-2"
                            onClick={() => handleEdit(item, 'general')}
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(item.sl_no, 'general')}
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                    
                    {generalInfo.length === 0 && (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          <i className="fas fa-info-circle fa-2x mb-2"></i>
                          <p>No general information found. Add your school details!</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="documents" title="Public Disclosure Documents">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Public Disclosure Documents</span>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                >
                  <i className="fas fa-plus me-1"></i> Add Document
                </Button>
              </Card.Header>
              <Card.Body>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="documents">
                    {(provided) => (
                      <Table responsive hover ref={provided.innerRef} {...provided.droppableProps}>
                        <thead>
                          <tr>
                            <th width="60">Drag</th>
                            <th width="60">Type</th>
                            <th>Document Name</th>
                            <th>File</th>
                            <th>Remarks</th>
                            <th>Order</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th width="150">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.map((doc, index) => (
                            <Draggable key={doc.doc_id} draggableId={doc.doc_id.toString()} index={index}>
                              {(provided) => (
                                <tr 
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <td {...provided.dragHandleProps}>
                                    <i className="fas fa-grip-vertical text-muted"></i>
                                  </td>
                                  <td>
                                    <i className={`fas ${getFileIcon(doc.file_path)} text-primary`}></i>
                                  </td>
                                  <td>
                                    <strong>{doc.doc_name}</strong>
                                  </td>
                                  <td>
                                    {doc.file_path ? (
                                      <a 
                                        href={`${API_BASE_URL.replace('/api', '')}${doc.file_path}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-decoration-none"
                                      >
                                        <small>
                                          <i className="fas fa-download me-1"></i>
                                          Download
                                        </small>
                                      </a>
                                    ) : (
                                      <span className="text-muted small">No file</span>
                                    )}
                                  </td>
                                  <td>
                                    <small className="text-muted">
                                      {truncateText(doc.remarks, 40)}
                                    </small>
                                  </td>
                                  <td>
                                    <Badge bg="secondary">{doc.display_order}</Badge>
                                  </td>
                                  <td>
                                    <Form.Check
                                      type="switch"
                                      checked={doc.is_active === true || doc.is_active === "1" || doc.is_active === 1}
                                      onChange={() => handleToggleActive(doc)}
                                      label={doc.is_active ? 'Active' : 'Inactive'}
                                      key={`active-${doc.doc_id}-${doc.is_active}`}
                                    />
                                  </td>
                                  <td>
                                    <small className="text-muted">
                                      {formatDate(doc.created_at)}
                                    </small>
                                  </td>
                                  <td>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      className="me-2"
                                      onClick={() => handleEdit(doc, 'documents')}
                                    >
                                      <i className="fas fa-edit"></i>
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleDelete(doc.doc_id, 'documents')}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </Button>
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </tbody>
                      </Table>
                    )}
                  </Droppable>
                </DragDropContext>
                
                {documents.length === 0 && (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-file-alt fa-3x mb-3"></i>
                    <p>No documents found. Add your public disclosure documents!</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size={activeTab === 'general' ? 'lg' : 'xl'}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingItem ? 'Edit' : 'Add'} {activeTab === 'general' ? 'General Information' : 'Document'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {activeTab === 'general' ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Title *</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., School Name"
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Information *</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="information"
                      value={formData.information}
                      onChange={handleInputChange}
                      rows={5}
                      required
                      placeholder="Enter the information details..."
                    />
                  </Form.Group>
                </>
              ) : (
                <>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>Document Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="doc_name"
                          value={formData.doc_name}
                          onChange={handleInputChange}
                          required
                          placeholder="e.g., Annual Report 2024-25"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Display Order</Form.Label>
                        <Form.Control
                          type="number"
                          name="display_order"
                          value={formData.display_order}
                          onChange={handleInputChange}
                          min="1"
                        />
                        <Form.Text className="text-muted">
                          Lower numbers appear first
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>Upload Document</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                          onChange={handleFileChange}
                          disabled={uploadingFile}
                        />
                        <Form.Text className="text-muted">
                          Upload PDF, Word, Excel, or image files (Max 5MB)
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <div>
                          <Form.Check
                            type="switch"
                            name="is_active"
                            label="Active"
                            checked={formData.is_active}
                            onChange={handleInputChange}
                          />
                        </div>
                        <Form.Text className="text-muted">
                          Inactive documents won't be shown publicly
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Remarks/Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Add any remarks or description about this document..."
                    />
                  </Form.Group>
                  
                  {editingItem && editingItem.file_path && !uploadedFile && (
                    <div className="alert alert-info">
                      <small>
                        <i className="fas fa-info-circle me-1"></i>
                        Current file: {editingItem.file_path.split('/').pop()}
                      </small>
                    </div>
                  )}
                  
                  {uploadedFile && (
                    <div className="alert alert-success">
                      <small>
                        <i className="fas fa-check-circle me-1"></i>
                        Selected file: {uploadedFile.name} ({Math.round(uploadedFile.size / 1024)} KB)
                      </small>
                    </div>
                  )}
                </>
              )}
              
              {editingItem && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <i className="fas fa-calendar-plus me-2"></i>
                    Created: {formatDate(editingItem.created_at)}
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingFile}>
                {uploadingFile && <Spinner animation="border" size="sm" className="me-2" />}
                {editingItem ? 'Update' : 'Add'} {activeTab === 'general' ? 'Information' : 'Document'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default SchoolInfoManager;